import React from 'react';
import type { FormattedString } from '../types';
import { buttonClassName, DEFAULT_SIZE, DEFAULT_VARIANT, ButtonStyling } from './styling';

/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-props-no-spreading */

interface IRCButtonProps extends ButtonStyling {
  /** The button's text. This is a ReactNode to allow icons and other _subtle_ text styling. */
  children: FormattedString;

  /** The click handler passed to the button */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /**
   * **NOTE** UX-wise it's often better to not render the button rather than disabling it.
   * If needed, this disables the button functionally and visually.
   */
  disabled?: boolean;

  /** Appends `type="submit"` to the button for use inside forms. */
  isSubmit?: boolean;

  /** title passthrough for the button element, should be used when wrapping an icon or something without visible text */
  title?: string;

  /** useful for when aria attributes need to refer to a button's id */
  id?: string;

  /** aria-disabled passthrough */
  'aria-disabled'?: boolean;

  /** form passthrough */
  form?: string;
}

export const RCButton = React.forwardRef<HTMLButtonElement, IRCButtonProps>(({
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  fullWidth,
  inverted,
  children,
  isSubmit,
  onClick,
  disabled,
  title,
  id,
  ...passthroughProps
}: IRCButtonProps, ref) => {
  return (
    <button
      className={buttonClassName({ variant, size, fullWidth, inverted })}
      type={isSubmit ? 'submit' : 'button'}
      onClick={onClick}
      disabled={disabled}
      title={title}
      id={id}
      ref={ref}
      {...passthroughProps}
    >
      {children}
    </button>
  );
});

// Explicitly setting displayName to help with debugging and tracing an otherwise anonymous function.
RCButton.displayName = 'RCButton';
